import React, { useMemo } from 'react';

import { Badge, Callout } from '@/components/UI';
import FormNumberInput from '@/components/UI/FormNumberInput';
import FormSelect from '@/components/UI/FormSelect';

import { OPTION_SEARCH_SORT_KEYS } from '@einhaus/project314-types';
import {
    formStateToOptionSearchConfig,
    getOptionSearchConfigAdvisories,
    OPTION_SEARCH_NUMERIC_FIELDS,
    type OptionSearchConfigFormState
} from './optionSearchConfigFormState';

const SORT_KEY_LABELS: Record<(typeof OPTION_SEARCH_SORT_KEYS)[number], string> = {
    strike: 'Strike',
    bidPrice: 'Bid Price',
    askPrice: 'Ask Price',
    mid: 'Mid Price',
    last: 'Last Price',
    daysToExpiration: 'Days To Expiration',
    impliedVolatility: 'Implied Volatility',
    premiumArorc: 'ARORC',
    putSellScore: 'Put Sell Score',
    probabilityOtm: 'Probability OTM',
    percentageOTM: '% OTM',
    percentageITM: '% ITM',
    volume: 'Volume',
    bidSize: 'Bid Size',
    askSize: 'Ask Size',
    openInterest: 'Open Interest'
};

interface RangeFieldProps {
    disabled: boolean;
    error?: string;
    helpText?: string;
    high: number | null;
    idBase: string;
    integer?: boolean;
    label: string;
    low: number | null;
    max?: number;
    min?: number;
    prefix?: string;
    step?: number;
    suffix?: string;
    onHighChange: (value: number | null) => void;
    onLowChange: (value: number | null) => void;
}

/** One filter as a "low – high" pair on a single labeled row; empty means the bound is off. */
const RangeField: React.FC<RangeFieldProps> = ({
    disabled,
    error,
    helpText,
    high,
    idBase,
    integer = false,
    label,
    low,
    max,
    min,
    prefix,
    step,
    suffix,
    onHighChange,
    onLowChange
}) => (
    <div className="mb-3">
        <label className="mb-1 block text-sm font-medium text-zinc-200" htmlFor={`${idBase}Low`}>
            {label}
        </label>
        <div className="flex items-center gap-2">
            <FormNumberInput
                noMargin
                aria-label={`${label} low`}
                disabled={disabled}
                id={`${idBase}Low`}
                integer={integer}
                max={max}
                min={min}
                placeholder="Any"
                prefix={prefix}
                step={step}
                suffix={suffix}
                value={low}
                onValueChange={onLowChange}
            />
            <span aria-hidden className="shrink-0 text-zinc-500">
                –
            </span>
            <FormNumberInput
                noMargin
                aria-label={`${label} high`}
                disabled={disabled}
                id={`${idBase}High`}
                integer={integer}
                max={max}
                min={min}
                placeholder="Any"
                prefix={prefix}
                step={step}
                suffix={suffix}
                value={high}
                onValueChange={onHighChange}
            />
        </div>
        {error ? (
            <div className="mt-1 text-xs text-red-400">{error}</div>
        ) : (
            helpText && <div className="mt-1 text-xs text-zinc-500">{helpText}</div>
        )}
    </div>
);

interface OptionSearchConfigFormProps {
    disabled?: boolean;
    errors: Record<string, string>;
    state: OptionSearchConfigFormState;
    /** Strategy-level Min DTE; bounds the chain fetch even in search mode (empty = 5-day default). */
    strategyMinDte: number | null;
    /** Strategy-level Max DTE; bounds the chain fetch even in search mode (empty = 30-day default). */
    strategyMaxDte: number | null;
    /** Strategy-level Max Spread %; also enforced on the final pick in search mode. */
    strategyMaxSpreadPercentage: number | null;
    onChange: (updates: Partial<OptionSearchConfigFormState>) => void;
}

/**
 * Structured editor for the option search config (buyingMethod
 * "optionSearchConfig"). Replaces the old free-form JSON textarea so field
 * names and value types are enforced instead of silently dropped by the
 * option finder.
 */
const OptionSearchConfigForm: React.FC<OptionSearchConfigFormProps> = ({
    disabled = false,
    errors,
    state,
    strategyMinDte,
    strategyMaxDte,
    strategyMaxSpreadPercentage,
    onChange
}) => {
    const previewJson = useMemo(() => JSON.stringify(formStateToOptionSearchConfig(state), null, 2), [state]);

    const advisories = useMemo(
        () => getOptionSearchConfigAdvisories(state, { strategyMinDte, strategyMaxDte, strategyMaxSpreadPercentage }),
        [state, strategyMinDte, strategyMaxDte, strategyMaxSpreadPercentage]
    );

    const activeFilterCount = useMemo(
        () => OPTION_SEARCH_NUMERIC_FIELDS.reduce((count, field) => (state[field] !== null ? count + 1 : count), 0),
        [state]
    );

    return (
        <div>
            {advisories.map((advisory) => (
                <Callout className="mb-3" key={advisory.message} variant={advisory.severity === 'warning' ? 'warning' : 'info'}>
                    {advisory.message}
                </Callout>
            ))}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6">
                <FormSelect
                    disabled={disabled}
                    id="oscOptionType"
                    label="Option Type"
                    options={[
                        { value: 'c', label: 'Call' },
                        { value: 'p', label: 'Put' }
                    ]}
                    value={state.optionType}
                    onChange={(e) => onChange({ optionType: e.target.value as OptionSearchConfigFormState['optionType'] })}
                />
                <FormSelect
                    disabled={disabled}
                    helpText="Candidates are ranked by this field; the best match is selected."
                    id="oscSortBy"
                    label="Rank By"
                    options={OPTION_SEARCH_SORT_KEYS.map((key) => ({ value: key, label: SORT_KEY_LABELS[key] }))}
                    value={state.sortBy}
                    onChange={(e) => onChange({ sortBy: e.target.value as OptionSearchConfigFormState['sortBy'] })}
                />
                <FormSelect
                    disabled={disabled}
                    id="oscSortOrder"
                    label="Rank Order"
                    options={[
                        { value: 'desc', label: 'Highest first' },
                        { value: 'asc', label: 'Lowest first' }
                    ]}
                    value={state.sortOrder}
                    onChange={(e) => onChange({ sortOrder: e.target.value as OptionSearchConfigFormState['sortOrder'] })}
                />
            </div>

            <div className="mt-1 mb-2 flex items-center gap-2">
                <span className="text-sm font-medium text-zinc-200">Filters</span>
                <Badge pill variant={activeFilterCount > 0 ? 'primary' : 'secondary'}>
                    {activeFilterCount} active
                </Badge>
                <span className="text-xs text-zinc-500">Leave a bound empty to disable it.</span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-x-6">
                <RangeField
                    integer
                    disabled={disabled}
                    error={errors.daysToExpirationLow}
                    idBase="oscDte"
                    label="Days To Expiration"
                    low={state.daysToExpirationLow}
                    high={state.daysToExpirationHigh}
                    min={0}
                    onHighChange={(daysToExpirationHigh) => onChange({ daysToExpirationHigh })}
                    onLowChange={(daysToExpirationLow) => onChange({ daysToExpirationLow })}
                />
                <RangeField
                    disabled={disabled}
                    error={errors.strikePriceLow}
                    idBase="oscStrike"
                    label="Strike Price"
                    low={state.strikePriceLow}
                    high={state.strikePriceHigh}
                    min={0}
                    prefix="$"
                    step={0.5}
                    onHighChange={(strikePriceHigh) => onChange({ strikePriceHigh })}
                    onLowChange={(strikePriceLow) => onChange({ strikePriceLow })}
                />
                <RangeField
                    disabled={disabled}
                    error={errors.premiumLow}
                    idBase="oscPremium"
                    label="Premium"
                    low={state.premiumLow}
                    high={state.premiumHigh}
                    min={0}
                    prefix="$"
                    step={0.05}
                    onHighChange={(premiumHigh) => onChange({ premiumHigh })}
                    onLowChange={(premiumLow) => onChange({ premiumLow })}
                />
                <RangeField
                    disabled={disabled}
                    error={errors.percentageOTMLow}
                    idBase="oscPercentageOtm"
                    label="% Out of the Money"
                    low={state.percentageOTMLow}
                    high={state.percentageOTMHigh}
                    step={0.1}
                    suffix="%"
                    onHighChange={(percentageOTMHigh) => onChange({ percentageOTMHigh })}
                    onLowChange={(percentageOTMLow) => onChange({ percentageOTMLow })}
                />
                <RangeField
                    disabled={disabled}
                    error={errors.percentageITMLow}
                    idBase="oscPercentageItm"
                    label="% In the Money"
                    low={state.percentageITMLow}
                    high={state.percentageITMHigh}
                    step={0.1}
                    suffix="%"
                    onHighChange={(percentageITMHigh) => onChange({ percentageITMHigh })}
                    onLowChange={(percentageITMLow) => onChange({ percentageITMLow })}
                />
                <RangeField
                    disabled={disabled}
                    error={errors.arorcLow}
                    helpText="Annualized rate of return on capital; 0.6 = 60%."
                    idBase="oscArorc"
                    label="ARORC"
                    low={state.arorcLow}
                    high={state.arorcHigh}
                    step={0.05}
                    onHighChange={(arorcHigh) => onChange({ arorcHigh })}
                    onLowChange={(arorcLow) => onChange({ arorcLow })}
                />
                <RangeField
                    disabled={disabled}
                    error={errors.putSellScoreLow}
                    idBase="oscPutSellScore"
                    label="Put Sell Score"
                    low={state.putSellScoreLow}
                    high={state.putSellScoreHigh}
                    min={0}
                    onHighChange={(putSellScoreHigh) => onChange({ putSellScoreHigh })}
                    onLowChange={(putSellScoreLow) => onChange({ putSellScoreLow })}
                />
                <RangeField
                    disabled={disabled}
                    error={errors.probabilityOTMLow}
                    helpText="0–1; 0.5 = 50%."
                    idBase="oscProbabilityOtm"
                    label="Probability OTM"
                    low={state.probabilityOTMLow}
                    high={state.probabilityOTMHigh}
                    max={1}
                    min={0}
                    step={0.05}
                    onHighChange={(probabilityOTMHigh) => onChange({ probabilityOTMHigh })}
                    onLowChange={(probabilityOTMLow) => onChange({ probabilityOTMLow })}
                />
            </div>

            <div className="text-sm font-medium text-zinc-200 mt-1 mb-1">Liquidity</div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-x-6">
                <FormNumberInput
                    disabled={disabled}
                    error={errors.maxSpreadAmount}
                    helpText="Maximum bid/ask spread."
                    id="oscMaxSpreadAmount"
                    label="Max Spread"
                    min={0}
                    placeholder="Any"
                    prefix="$"
                    step={0.05}
                    value={state.maxSpreadAmount}
                    onValueChange={(maxSpreadAmount) => onChange({ maxSpreadAmount })}
                />
                <FormNumberInput
                    integer
                    disabled={disabled}
                    error={errors.bidSizeMin}
                    id="oscBidSizeMin"
                    label="Min Bid Size"
                    min={0}
                    placeholder="Any"
                    value={state.bidSizeMin}
                    onValueChange={(bidSizeMin) => onChange({ bidSizeMin })}
                />
                <FormNumberInput
                    integer
                    disabled={disabled}
                    error={errors.askSizeMin}
                    id="oscAskSizeMin"
                    label="Min Ask Size"
                    min={0}
                    placeholder="Any"
                    value={state.askSizeMin}
                    onValueChange={(askSizeMin) => onChange({ askSizeMin })}
                />
                <FormNumberInput
                    integer
                    disabled={disabled}
                    error={errors.volumeMin}
                    id="oscVolumeMin"
                    label="Min Volume"
                    min={0}
                    placeholder="Any"
                    value={state.volumeMin}
                    onValueChange={(volumeMin) => onChange({ volumeMin })}
                />
                <FormNumberInput
                    integer
                    disabled={disabled}
                    error={errors.openInterestMin}
                    id="oscOpenInterestMin"
                    label="Min Open Interest"
                    min={0}
                    placeholder="Any"
                    value={state.openInterestMin}
                    onValueChange={(openInterestMin) => onChange({ openInterestMin })}
                />
            </div>

            <details className="mt-2">
                <summary className="text-sm text-zinc-400 cursor-pointer select-none">Config preview (JSON)</summary>
                <pre className="mt-1 rounded bg-zinc-900 border border-zinc-700 p-3 text-xs text-zinc-300 overflow-x-auto">
                    {previewJson}
                </pre>
            </details>
        </div>
    );
};

export default OptionSearchConfigForm;
